/**
 * My Plans — seasonal plant-week outlook helpers (Phase C).
 * Aggregate by planting_season; near-term forecast only when in/near the window.
 */

export const PLANT_WEEK_NEAR_LEAD_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parsePlanDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const raw = String(value).trim();
  if (!raw) return null;
  // Prefer ISO date-only to avoid TZ shifting calendar days.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function startOfLocalDay(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((startOfLocalDay(b) - startOfLocalDay(a)) / MS_PER_DAY);
}

/**
 * @returns {'far'|'near'|'in'|'unknown'}
 */
export function resolveWindowProximity(windowStart, windowEnd, today = new Date(), nearLeadDays = PLANT_WEEK_NEAR_LEAD_DAYS) {
  const start = parsePlanDate(windowStart);
  const end = parsePlanDate(windowEnd);
  const now = startOfLocalDay(today);
  if (!start || !end) return 'unknown';
  if (now >= start && now <= end) return 'in';
  if (now < start) {
    const daysUntil = daysBetween(now, start);
    if (daysUntil != null && daysUntil <= nearLeadDays) return 'near';
    return 'far';
  }
  return 'far'; // past window
}

export function formatShortWindowDate(value) {
  const d = parsePlanDate(value);
  if (!d) return '';
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

export function resolveSeasonOutlookPoint(plans = []) {
  const list = Array.isArray(plans) ? plans : [];
  // Prefer explicit place coords across the season group, then centroids.
  for (const plan of list) {
    const lat = Number(plan?.place_latitude);
    const lng = Number(plan?.place_longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { latitude: lat, longitude: lng, sourcePlanId: plan.id };
    }
  }
  for (const plan of list) {
    const cLat = Number(plan?.centroid_lat);
    const cLng = Number(plan?.centroid_lng);
    if (Number.isFinite(cLat) && Number.isFinite(cLng)) {
      return { latitude: cLat, longitude: cLng, sourcePlanId: plan.id };
    }
  }
  return null;
}

export function pickSeasonDrainage(plans = []) {
  const list = Array.isArray(plans) ? plans : [];
  for (const plan of list) {
    const d = String(plan?.soil_drainage || '').trim();
    if (d) return d;
  }
  return '';
}

export function pickSeasonZone(plans = []) {
  const list = Array.isArray(plans) ? plans : [];
  for (const plan of list) {
    const z = String(plan?.usda_zone_code || '').trim();
    if (z) return z;
  }
  return '';
}

export function pickSeasonFrost(plans = []) {
  const list = Array.isArray(plans) ? plans : [];
  const manual = list.find((plan) => String(plan?.frost_source || '').toLowerCase() === 'manual');
  const plan = manual || list.find((item) => item?.last_frost_label || item?.first_frost_label);
  if (!plan) {
    return { frostSource: '', lastFrostLabel: '', firstFrostLabel: '' };
  }
  return {
    frostSource: String(plan.frost_source || '').trim().toLowerCase(),
    lastFrostLabel: String(plan.last_frost_label || '').trim(),
    firstFrostLabel: String(plan.first_frost_label || '').trim(),
  };
}

/**
 * Group saved plans into fall/spring outlook shells (no network).
 */
export function buildSeasonOutlookGroups(plans = [], { today = new Date(), nearLeadDays = PLANT_WEEK_NEAR_LEAD_DAYS } = {}) {
  const buckets = { fall: [], spring: [] };
  (Array.isArray(plans) ? plans : []).forEach((plan) => {
    const season = String(plan?.planting_season || '').toLowerCase();
    if (season === 'fall' || season === 'spring') buckets[season].push(plan);
  });

  return ['fall', 'spring']
    .filter((season) => buckets[season].length > 0)
    .map((season) => {
      const seasonPlans = buckets[season];
      const sample = seasonPlans.find((p) => p.window_start && p.window_end) || seasonPlans[0];
      const proximity = resolveWindowProximity(sample?.window_start, sample?.window_end, today, nearLeadDays);
      const point = resolveSeasonOutlookPoint(seasonPlans);
      const frost = pickSeasonFrost(seasonPlans);
      return {
        season,
        seasonLabel: season === 'spring' ? 'SPRING' : 'FALL',
        planCount: seasonPlans.length,
        plans: seasonPlans,
        windowStart: sample?.window_start || null,
        windowEnd: sample?.window_end || null,
        windowLabel: formatShortWindowDate(sample?.window_start) && formatShortWindowDate(sample?.window_end)
          ? `${formatShortWindowDate(sample.window_start)} – ${formatShortWindowDate(sample.window_end)}`
          : '',
        proximity,
        point,
        usdaZoneCode: pickSeasonZone(seasonPlans),
        soilDrainage: pickSeasonDrainage(seasonPlans),
        frostSource: frost.frostSource,
        lastFrostLabel: frost.lastFrostLabel,
        firstFrostLabel: frost.firstFrostLabel,
        needsForecast: proximity === 'in' || proximity === 'near',
      };
    });
}

/**
 * Summarize planting-forecast payload for a season strip.
 */
export function summarizePlantWeekForecast(forecastPayload, { limit = 3 } = {}) {
  if (!forecastPayload || !forecastPayload.weather_ok) {
    return {
      ok: false,
      error: forecastPayload?.weather_error || 'Forecast unavailable',
      workDays: [],
      bestDay: null,
    };
  }
  const days = Array.isArray(forecastPayload.days) ? forecastPayload.days : [];
  const inWindow = days.filter((d) => d.in_window);
  const pool = (inWindow.length ? inWindow : days)
    .slice()
    .sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
  const workDays = pool
    .filter((d) => (Number(d.score) || 0) >= 55)
    .slice(0, limit)
    .map((d) => ({
      date: d.date,
      label: d.label || d.date,
      score: d.score,
      rainLabel: d.rain_label,
      isBest: Boolean(d.is_best),
    }));
  const best = forecastPayload.best_day && !forecastPayload.best_day.outside_window
    ? forecastPayload.best_day
    : (workDays[0] || null);

  return {
    ok: true,
    error: null,
    workDays,
    bestDay: best,
    why: forecastPayload.best_day?.why || '',
  };
}

export function buildQuietOutlookCopy(group) {
  if (!group) return '';
  if (!group.point) {
    return 'Add a town or map plot on a plan so we can score work days for this season.';
  }
  if (group.proximity === 'far' && group.windowStart) {
    return `Window opens ${formatShortWindowDate(group.windowStart)}. We’ll flag good work days when you’re inside or near the band.`;
  }
  if (group.proximity === 'far') {
    return 'Outside the planting window for this season.';
  }
  if (group.proximity === 'unknown') {
    if (!group.usdaZoneCode) {
      return 'Need a USDA zone on these plans to unlock the planting window — confirm zone on Ground and re-save.';
    }
    return 'Planting window not set yet — re-save a plan to refresh window dates.';
  }
  return '';
}
